// src/components/manuscript/StructurePreview.jsx
//
// The right-hand column: the selected element drawn as the page it produces —
// an open interior spread for Manuscript Editor elements, a wraparound cover
// mockup for the two that belong to Designer's Pages.
//
// **Why this does not use BookPreviewModal.** That component is a full-screen
// read-only overlay that pours the whole cleaned manuscript HTML into StPageFlip
// and lets you flip through it; its page list is the manuscript. This shows one
// template at a time on a fixed two-leaf spread, and "turning the page" means
// selecting a different element. Driving StPageFlip from outside its own page
// list would mean re-initialising it on every selection. The page geometry is
// the part worth sharing, and it is shared: the leaf is the book's own trim
// size, from the same `bookSize` table BookPreviewModal measures its sheets
// with, so a 6 x 9 book is not previewed here on an 8.5 x 11 page.
//
// The leaf is sized by `aspect-ratio` rather than measured and scaled, because
// nothing here needs to match a pixel grid — there is no flip library holding a
// box of its own, so the CSS can simply do it.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { SP, FONT } from './structureTokens';
import { DEFAULT_BOOK_SIZE, bookSize, dimensionsLabel } from '../../config/bookSizes';
import PreviewContent, { CoverFrontTitle, CoverFrontFooter, CoverSpecPanel, contentSx } from './previewRenderers';

// Matches the CSS animation below. The content swaps at the midpoint, while the
// leaf is edge-on and nothing can be read.
const FLIP_MS = 560;
const FLIP_SWAP_MS = 270;

/** A single leaf, shaped to the book's trim size. */
function leafSxFor(trimSize) {
  const { widthIn, heightIn } = bookSize(trimSize);
  return {
    flex: 1,
    aspectRatio: `${widthIn} / ${heightIn}`,
    backgroundColor: '#FAF7EF',
    border: '1px solid rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    p: '30px 26px',
  };
}

function justifyFor(position) {
  if (position === 'top') return 'flex-start';
  if (position === 'bottom') return 'flex-end';
  return 'center';
}

function HiddenNote() {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
      gap: 1,
      fontFamily: FONT.mono,
      fontSize: 11,
      color: SP.gray,
    }}>
      <VisibilityOffIcon sx={{ fontSize: 24, opacity: 0.4 }} />
      This element is turned off — hidden from the finished book
    </Box>
  );
}

function NavButton({ label, onClick, disabled, children }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1.6,
        py: 0.9,
        fontFamily: FONT.mono,
        fontSize: 10.5,
        borderRadius: '3px',
        border: '1px solid rgba(255,255,255,0.3)',
        backgroundColor: 'transparent',
        color: disabled ? 'rgba(255,255,255,0.3)' : SP.white,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all .2s ease',
        ...(disabled ? {} : { '&:hover': { borderColor: SP.gold, color: SP.goldLight } }),
      }}
    >
      {children}
    </Box>
  );
}

export default function StructurePreview({ element, state, onPrev, onNext, navDisabled, trimSize = DEFAULT_BOOK_SIZE }) {
  // The flip plays on the leaf; the content it shows swaps halfway through, so a
  // reader never sees the new page slide in flat.
  const [shown, setShown] = useState({ element, state });
  const [flipping, setFlipping] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    // Only the element changing is a page turn. Typing into the current one has
    // to reach the preview immediately, or the live preview is not live.
    if (element.id === shown.element.id) {
      setShown({ element, state });
      return undefined;
    }

    setFlipping(true);
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => setShown({ element, state }), FLIP_SWAP_MS),
      setTimeout(() => setFlipping(false), FLIP_MS),
    ];
    return () => timers.current.forEach(clearTimeout);
  }, [element, state, shown.element.id]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Whether the page's content is taller than the leaf — a long contents list, a
  // long preface. The print runs such a section on to the next page; the leaf
  // shows its first page and says so, rather than cutting it off without a word.
  // Measured, not computed: what fits depends on the leaf's size at this width,
  // the trim size and the author's type size.
  const contentRef = useRef(null);
  const [runsOn, setRunsOn] = useState(false);
  useLayoutEffect(() => {
    const box = contentRef.current;
    if (!box) {
      setRunsOn(false);
      return undefined;
    }
    const measure = () => {
      const content = box.firstElementChild;
      setRunsOn(Boolean(content) && content.offsetHeight > box.clientHeight + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    if (box.firstElementChild) observer.observe(box.firstElementChild);
    return () => observer.disconnect();
  }, [shown]);

  const isCover = shown.element.loc === 'cover';
  const enabled = shown.state.enabled;

  const flipSx = flipping ? {
    animation: `rvStructureFlip ${FLIP_MS}ms ease-in-out`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  } : {};

  return (
    // overflow:hidden because the two Glow circles below are positioned with
    // negative offsets (right: -60) and drift a further 18px on their
    // animation. Unclipped they push the document 36px wider than the window
    // and the whole page scrolls sideways — at every width, not just small
    // ones. They are meant to bleed off the edge of the stage; this is what
    // makes "off the edge" mean the stage rather than the viewport.
    <Box sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {/* the keyframes the leaf animates with */}
      <Box sx={{
        '@keyframes rvStructureFlip': {
          '0%': { transform: 'rotateY(0deg)' },
          '48%': { transform: 'rotateY(-92deg)', filter: 'brightness(.8)' },
          '52%': { transform: 'rotateY(-92deg)', filter: 'brightness(.8)' },
          '100%': { transform: 'rotateY(0deg)' },
        },
      }} />

      {/* soft drifting light, so the stage is not a flat rectangle */}
      <Glow sx={{
        width: 280, height: 280, top: -60, right: -60, opacity: 0.28,
        background: `radial-gradient(circle, ${SP.goldLight} 0%, transparent 70%)`,
      }} />
      <Glow sx={{
        width: 240, height: 240, bottom: -60, left: -40, opacity: 0.3,
        animationDelay: '-6s',
        background: `radial-gradient(circle, ${SP.purpleMid} 0%, transparent 70%)`,
      }} />

      <Box sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.75,
      }}>
        {/* `div`, not the default `p`: the pulse dot below is a Box, and a div
            inside a paragraph is invalid — React warns, and the browser's parser
            hoists the dot out of the paragraph on every render. */}
        <Typography component="div" sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: FONT.mono,
          fontSize: 11,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: SP.white,
          textShadow: '0 1px 3px rgba(0,0,0,0.4)',
        }}>
          <Box sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: '#5FCB84',
            animation: 'rvStructurePulse 1.8s ease-in-out infinite',
            '@keyframes rvStructurePulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }} />
          Live preview
        </Typography>
        <Typography sx={{
          fontFamily: FONT.mono,
          fontSize: 10.5,
          color: 'rgba(255,255,255,0.85)',
          textShadow: '0 1px 3px rgba(0,0,0,0.4)',
        }}>
          {isCover ? 'Wraparound cover mockup' : `Interior spread · ${dimensionsLabel(trimSize)}`}
        </Typography>
      </Box>

      <Box sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        gap: 1.25,
        mb: 1.75,
      }}>
        <NavButton label="Previous page" onClick={onPrev} disabled={navDisabled}>
          <ChevronLeftIcon sx={{ fontSize: 15 }} /> Prev page
        </NavButton>
        <NavButton label="Next page" onClick={onNext} disabled={navDisabled}>
          Next page <ChevronRightIcon sx={{ fontSize: 15 }} />
        </NavButton>
      </Box>

      {isCover ? (
        <Box sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          maxWidth: 460,
          mx: 'auto',
          aspectRatio: '7.4 / 11',
          perspective: '1600px',
          filter: 'drop-shadow(0 26px 40px rgba(20,10,30,0.45))',
        }}>
          <Box sx={{
            width: '11%',
            backgroundColor: SP.purpleDeep,
            borderRadius: '3px 0 0 3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography sx={{
              fontFamily: FONT.mono,
              fontSize: 9,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.6)',
              writingMode: 'vertical-rl',
            }}>
              {!enabled ? '—' : (shown.element.preview === 'cover-front'
                ? (shown.state.fields?.title || 'Title')
                : 'Spec')}
            </Typography>
          </Box>

          <Box sx={{
            flex: 1,
            borderRadius: '0 3px 3px 0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transformOrigin: 'left center',
            backfaceVisibility: 'hidden',
            ...(shown.element.preview === 'cover-front' && enabled
              ? {
                background: `linear-gradient(165deg, #2E285F 0%, ${SP.purpleDeep} 60%, #17133A 100%)`,
                textAlign: 'center',
                p: '12% 10%',
              }
              : { backgroundColor: SP.white, p: '24px 22px' }),
            ...flipSx,
          }}>
            {!enabled && <HiddenNote />}

            {enabled && shown.element.preview === 'cover-front' && (
              <>
                {/* Same rule as the interior leaf: the title block owns the
                    Position choice, and the author credit sits at the foot of
                    the cover rather than competing for the free space. */}
                <Box sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: justifyFor(shown.state.position),
                  ...contentSx(shown.state.format),
                  textAlign: shown.state.format?.align || 'center',
                }}>
                  <CoverFrontTitle state={shown.state} />
                </Box>
                <Box sx={{ flexShrink: 0, pt: 1.5, ...contentSx(shown.state.format), textAlign: 'center' }}>
                  <CoverFrontFooter state={shown.state} />
                </Box>
              </>
            )}

            {enabled && shown.element.preview === 'cover-spec' && (
              <Box sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: justifyFor(shown.state.position),
                ...contentSx(shown.state.format),
              }}>
                <Box><CoverSpecPanel state={shown.state} /></Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          gap: '3px',
          maxWidth: 560,
          mx: 'auto',
          perspective: '1600px',
          filter: 'drop-shadow(0 26px 40px rgba(20,10,30,0.45))',
        }}>
          {/* verso — the facing leaf, always blank. It exists so the recto reads
              as the right-hand page of an open book rather than a loose sheet. */}
          <Box sx={{
            ...leafSxFor(trimSize),
            position: 'relative',
            borderRadius: '3px 0 0 3px',
            boxShadow: 'inset -8px 0 14px -10px rgba(0,0,0,0.3)',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography sx={{
              fontFamily: FONT.mono,
              fontSize: 9,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: 'rgba(20,18,27,0.28)',
              writingMode: 'vertical-rl',
            }}>
              Revisionari
            </Typography>
            <Typography sx={{
              position: 'absolute',
              bottom: 30,
              fontFamily: FONT.mono,
              fontSize: 9.5,
              color: 'rgba(20,18,27,0.35)',
            }}>
              {shown.element.order + 2}
            </Typography>
          </Box>

          {/* recto — the element itself */}
          <Box sx={{
            ...leafSxFor(trimSize),
            borderRadius: '0 3px 3px 0',
            boxShadow: 'inset 8px 0 14px -10px rgba(0,0,0,0.3)',
            transformOrigin: 'left center',
            backfaceVisibility: 'hidden',
            ...flipSx,
          }}>
            {enabled ? (
              <>
                {/* The content box owns the vertical placement. The page number
                    must stay outside it: inside, its `margin-top: auto` would
                    eat the free space and Top / Center / Bottom would do
                    nothing — which is exactly what it did.

                    Content that runs on starts at the top, as it does in print:
                    centred, it would overflow both edges and lose its opening
                    lines as well as its last. */}
                <Box
                  ref={contentRef}
                  sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: runsOn ? 'flex-start' : justifyFor(shown.state.position),
                    ...contentSx(shown.state.format),
                  }}
                >
                  <PreviewContent element={shown.element} state={shown.state} />
                  {runsOn && (
                    <Box sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: '2.5em',
                      pointerEvents: 'none',
                      background: 'linear-gradient(to bottom, rgba(250,247,239,0), #FAF7EF)',
                    }} />
                  )}
                </Box>
                <Box sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: runsOn ? 'space-between' : 'flex-end',
                  gap: 1,
                  pt: 1,
                  fontFamily: FONT.mono,
                  fontSize: 9.5,
                }}>
                  {/* One short line, never wrapped: on a phone the leaf is a few
                      dozen pixels wide, and the full sentence wrapped to three
                      lines and took the space the list needed. A screen reader
                      gets the sentence instead — an aria-label on a span is
                      ignored, and the arrow reads as "right arrow". */}
                  {runsOn && (
                    <Box
                      component="span"
                      title="Continues on the next page"
                      sx={{
                        color: SP.purple,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <span aria-hidden="true">Continues →</span>
                      <Box component="span" sx={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        p: 0,
                        m: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                      }}>
                        Continues on the next page
                      </Box>
                    </Box>
                  )}
                  <Box component="span" sx={{ color: 'rgba(20,18,27,0.4)' }}>
                    {shown.element.order + 3}
                  </Box>
                </Box>
              </>
            ) : <HiddenNote />}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Glow({ sx }) {
  return (
    <Box sx={{
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(70px)',
      pointerEvents: 'none',
      animation: 'rvStructureDrift 14s ease-in-out infinite alternate',
      '@keyframes rvStructureDrift': {
        '0%': { transform: 'translate(0, 0) scale(1)' },
        '100%': { transform: 'translate(18px, -14px) scale(1.08)' },
      },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      ...sx,
    }} />
  );
}
